/* eslint-disable  @typescript-eslint/no-explicit-any */
import {BehaviorSubject, filter, firstValueFrom, map, Observable} from 'rxjs';
import type {FilterRequest} from '../../../../../../models/cose-api/request/query-body-request.model';
import type {FacetsOptionsResponse} from '../../../../../../models/cose-api/response/facet-options.model';
import type {FilterNodeRequestUnique} from '../../../../../../utils/services/facets/facets-v2.service';
import type {FilterNode} from '../../../filter-node';
import type {FacetResponse} from '../../../../../../models/cose-api/response/facet-options.model';
import {FacetsV2Service} from '../../../../../../utils/services/facets/facets-v2.service';
import type {FacetChildUiModel} from '../../../../../../models/ui/facets/facet-tree-ui.model';
import {cloneObjectWithNoReference} from '../../../../../../utils/helpers/clone-object-with-no-reference';
import {CoseRequestService} from '../../../../../../utils/services/cose/cose-request.service';

export class FacetsMobileService {
  facetsState$: BehaviorSubject<FilterNodeRequestUnique[]> = new BehaviorSubject([]);
  facetsRequestState$: BehaviorSubject<FilterRequest[]> = new BehaviorSubject([]);
  facetsOptions$: BehaviorSubject<FacetsOptionsResponse>;
  facetChipsState$: BehaviorSubject<FilterNode[]> = new BehaviorSubject([]);
  facetLocalChipsState$: BehaviorSubject<FilterNode[]> = new BehaviorSubject([]);

  private static instancesMap: Map<string,FacetsMobileService> = new Map();

  constructor() {
      this.facetsState$ = new BehaviorSubject([]);
      this.facetsOptions$ = new BehaviorSubject(null);
  }

  get facetsState(): Observable<any[]> {
      return this.facetsState$.asObservable();
  }

  get facetsRequestState(): Observable<any[]> {
      return this.facetsRequestState$.asObservable();
  }

  get facetsOptions(): Observable<FacetsOptionsResponse> {
      return this.facetsOptions$.asObservable();
  }

  get facets(): Observable<FacetResponse[]> {
      return this.facetsOptions$
        .pipe(
          filter((fOpt) => !!fOpt),
          map((fOpt:FacetsOptionsResponse) => {
              return fOpt.facets || [];
          }),
        );
  }

  get facetsChipsState(): Observable<FilterNode[]> {
      return this.facetChipsState$;
  }

  get facetLocalChipsState(): Observable<FilterNode[]> {
      return this.facetLocalChipsState$.asObservable();
  }

  setFacetsState(facets: FilterNodeRequestUnique[]) {
      this.facetsState$.next(facets);
  }

  setFacetsRequestState(facets: FilterNodeRequestUnique[]) {
      let clearFacets = [];
      if(facets.length > 0){
        clearFacets = FacetsV2Service.clearTreeUnique(facets);
      }
      this.facetsRequestState$.next(clearFacets);
  }

  setFacets(facets: FacetsOptionsResponse) {
      this.facetsOptions$.next(facets);
  }

  setFacetsChipsState(filterNodes: FilterNode[]) {
      this.facetChipsState$.next(filterNodes);
  }

  setFacetLocalChipsState(filterNodes: FilterNode[]) {
      this.facetLocalChipsState$.next(filterNodes);
  }

  onFacetChange(filterNode: FacetChildUiModel, facetsState, facets, searchFacet = false, dividerMobileFacets = [], isFlatFacets = false) {
      let {filterNodeData, tree}
        = FacetsV2Service.facetChangeBeforeBuildQuery(
          filterNode,
          facetsState,
          facets,
          searchFacet,
          dividerMobileFacets,
          {},
          isFlatFacets
        );

      if(dividerMobileFacets && dividerMobileFacets.length > 0) {
        const {clearedQueryFilters, clearChange}
          = CoseRequestService.clearWhenDividerFacest(tree, dividerMobileFacets);

        if(clearChange) {
          tree = FacetsV2Service.setUnique(clearedQueryFilters);
        }
      }

      this.addOrRemoveLocalChipOnState(filterNodeData as unknown as FilterNode, filterNodeData.selected);
      this.addOrRemoveChipOnState(filterNodeData as unknown as FilterNode, filterNodeData.selected);
      this.setFacetsState(tree as FilterNodeRequestUnique[]);
      this.setFacetsRequestState(cloneObjectWithNoReference<any>(tree));
  }


  async createUpdateChipsStateFromResponseTree(tree): Promise<void> {
      const facetsChipsState = await firstValueFrom(this.facetsChipsState);
      const facetsRequestTreeUnique = FacetsV2Service.setUnique([...tree]);

      facetsRequestTreeUnique.forEach((facet) => {
          this.findFilterUpdateChipsState(facet.children, facetsChipsState);
      });
  }

  findFilterUpdateChipsState(filters, facetsChipsState) {
      filters.forEach(filter => {
          if (filter.selected && !facetsChipsState.some(f => f.uniqueId === filter.uniqueId)) {
            this.addOrRemoveChipOnState({
              uniqueId: filter.uniqueId,
              displayName: filter.displayName,
              value: filter.value,
              count: filter.count,
              children: filter.children || [],
              type: filter.type,
              selected: filter.selected
            }, true);
          } else if (!filter.selected && facetsChipsState.some(f => f.uniqueId === filter.uniqueId)) {
            this.addOrRemoveChipOnState({
              uniqueId: filter.uniqueId,
              displayName: filter.displayName,
              value: filter.value,
              count: filter.count,
              children: filter.children || [],
              type: filter.type,
              selected: filter.selected
            }, false);
          }

          if (filter.children.length > 0) {
            this.findFilterUpdateChipsState(filter.children, facetsChipsState);
          }
      });
  }

  //if selected=true - add to state, if selected=false - remove from state
  addOrRemoveChipOnState(filterNode: FilterNode, selected: boolean): void {
      if(filterNode.uniqueId) {
        const currentFacetChipsState = this.facetChipsState$.value;
        if(selected) {
          const isFacetChipsExists = currentFacetChipsState.some((chips)=> chips.uniqueId === filterNode.uniqueId);
          if(!isFacetChipsExists) {
            currentFacetChipsState.push(filterNode);
          }
        } else {
          const chipsIndex = currentFacetChipsState.findIndex((chips)=> chips.uniqueId === filterNode.uniqueId);
          if(chipsIndex !== -1) {
            currentFacetChipsState.splice(chipsIndex, 1);
          }
        }

        this.setFacetsChipsState(currentFacetChipsState);
      }
  }


  addOrRemoveLocalChipOnState(filterNode: FilterNode, selected: boolean): void {
      if(filterNode.uniqueId) {
        const currentFacetChipsState = this.facetLocalChipsState$.value;
        if(selected) {
          const isFacetChipsExists = currentFacetChipsState.some((chips)=> chips.uniqueId === filterNode.uniqueId);
          if(!isFacetChipsExists) {
            currentFacetChipsState.push(filterNode);
          }
        } else {
          const chipsIndex = currentFacetChipsState.findIndex((chips)=> chips.uniqueId === filterNode.uniqueId);
          if(chipsIndex !== -1) {
            currentFacetChipsState.splice(chipsIndex, 1);
          }
        }

        this.setFacetLocalChipsState(currentFacetChipsState);
      }
  }


  static getSetInstance(key: string) {
      const instance = new this();
      FacetsMobileService.instancesMap.set(key, instance);
      return instance;
  }

  static getInstance(key: string): FacetsMobileService {
      return FacetsMobileService.instancesMap.has(key) ? FacetsMobileService.instancesMap.get(key) : new this();
  }
}
